import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isLoggedIn, openLogin, openSignup, logout } from '../auth.js';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check authentication status only - NO automatic redirect
    const checkStatus = () => {
      const loggedIn = isLoggedIn();
      setIsAuthenticated(loggedIn);
    };

    checkStatus();
    
    // Listen for auth changes
    const interval = setInterval(checkStatus, 1000);
    
    // Force Netlify Identity close button to be visible AND inject custom close button
    const forceCloseButtonVisibility = () => {
      // First, try to find and style existing close buttons
      const closeButtons = document.querySelectorAll('.netlify-identity-modal .close, .netlify-identity-modal .close-button, .netlify-identity-modal .modal-close, .netlify-identity-modal .btn-close, .netlify-identity-modal button[aria-label="Close"], .netlify-identity-modal button[aria-label="close"]');
      closeButtons.forEach(button => {
        if (button instanceof HTMLElement) {
          button.style.setProperty('display', 'block', 'important');
          button.style.setProperty('visibility', 'visible', 'important');
          button.style.setProperty('opacity', '1', 'important');
          button.style.setProperty('position', 'absolute', 'important');
          button.style.setProperty('top', '15px', 'important');
          button.style.setProperty('right', '15px', 'important');
          button.style.setProperty('width', '35px', 'important');
          button.style.setProperty('height', '35px', 'important');
          button.style.setProperty('background', 'var(--primary)', 'important');
          button.style.setProperty('color', 'white', 'important');
          button.style.setProperty('border', 'none', 'important');
          button.style.setProperty('border-radius', '50%', 'important');
          button.style.setProperty('font-size', '20px', 'important');
          button.style.setProperty('line-height', '1', 'important');
          button.style.setProperty('cursor', 'pointer', 'important');
          button.style.setProperty('z-index', '10001', 'important');
          button.style.setProperty('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)', 'important');
        }
      });

      // If no close button exists, inject a custom one
      const modal = document.querySelector('.netlify-identity-modal');
      if (modal && !modal.querySelector('.custom-close-btn')) {
        const customCloseBtn = document.createElement('button');
        customCloseBtn.className = 'custom-close-btn';
        customCloseBtn.innerHTML = '✕';
        customCloseBtn.style.cssText = `
          position: absolute !important;
          top: 15px !important;
          right: 15px !important;
          width: 35px !important;
          height: 35px !important;
          background: #6366f1 !important;
          color: white !important;
          border: none !important;
          border-radius: 50% !important;
          font-size: 20px !important;
          line-height: 1 !important;
          cursor: pointer !important;
          z-index: 10001 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        `;
        
        customCloseBtn.addEventListener('click', () => {
          // Try to close the modal using Netlify Identity API
          if ((window as any).netlifyIdentity) {
            (window as any).netlifyIdentity.close();
          }
          // Also try to hide the modal directly
          if (modal instanceof HTMLElement) {
            modal.style.display = 'none';
            modal.style.visibility = 'hidden';
          }
        });
        
        modal.appendChild(customCloseBtn);
      }
    };
    
    // Force Netlify modal to cover entire screen and be above navbar
    const forceModalPositioning = () => {
      const modals = document.querySelectorAll('.netlify-identity-modal, [data-netlify-identity], .netlify-identity-container, .netlify-identity-widget, div[class*="netlify"], div[class*="identity"]');
      
      modals.forEach(modal => {
        if (modal instanceof HTMLElement) {
          // Force full screen coverage
          modal.style.setProperty('position', 'fixed', 'important');
          modal.style.setProperty('top', '0', 'important');
          modal.style.setProperty('left', '0', 'important');
          modal.style.setProperty('right', '0', 'important');
          modal.style.setProperty('bottom', '0', 'important');
          modal.style.setProperty('width', '100vw', 'important');
          modal.style.setProperty('height', '100vh', 'important');
          modal.style.setProperty('z-index', '999999', 'important');
          modal.style.setProperty('background', 'rgba(0, 0, 0, 0.9)', 'important');
          modal.style.setProperty('margin', '0', 'important');
          modal.style.setProperty('padding', '0', 'important');
          
          // Force all child elements to be properly positioned
          const children = modal.querySelectorAll('> div, > iframe');
          children.forEach(child => {
            if (child instanceof HTMLElement) {
              child.style.setProperty('position', 'relative', 'important');
              child.style.setProperty('top', 'auto', 'important');
              child.style.setProperty('left', 'auto', 'important');
              child.style.setProperty('transform', 'none', 'important');
              child.style.setProperty('margin', '1rem', 'important');
              child.style.setProperty('margin-top', '2rem', 'important');
              child.style.setProperty('padding', '2rem', 'important');
              child.style.setProperty('background', 'white', 'important');
              child.style.setProperty('border-radius', '20px', 'important');
              child.style.setProperty('box-shadow', '0 20px 60px rgba(0, 0, 0, 0.3)', 'important');
              child.style.setProperty('max-width', '90vw', 'important');
              child.style.setProperty('max-height', '85vh', 'important');
              child.style.setProperty('overflow-y', 'auto', 'important');
            }
          });
        }
      });
    };

    // Check for close button every 300ms when modal might be open
    const closeButtonInterval = setInterval(forceCloseButtonVisibility, 300);
    
    // Also continuously check and force modal positioning
    const modalPositioningInterval = setInterval(forceModalPositioning, 200);
    
    // Also listen for modal open events
    const handleModalOpen = () => {
      // Wait a bit for the modal to fully render
      setTimeout(() => {
        forceCloseButtonVisibility();
        // Force modal positioning to cover entire screen
        forceModalPositioning();
      }, 100);
    };
    
    // Listen for modal open events
    document.addEventListener('click', (e) => {
      if (e.target && (e.target as HTMLElement).closest('[data-netlify-identity]')) {
        setTimeout(handleModalOpen, 100);
      }
    });
    
    // Also check when the modal appears in the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement && node.classList.contains('netlify-identity-modal')) {
              setTimeout(handleModalOpen, 100);
            }
          });
        }
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearInterval(interval);
      clearInterval(closeButtonInterval);
      clearInterval(modalPositioningInterval);
      observer.disconnect();
    };
  }, []);

  // Function to handle continuing to site without login
  const handleContinueToSite = () => {
    // Close the modal if it's open
    if ((window as any).netlifyIdentity) {
      (window as any).netlifyIdentity.close();
    }
    // Set a flag in localStorage to remember user's choice
    localStorage.setItem('continueWithoutLogin', 'true');
    // The modal won't show again for this session
  };

  // Function to reset login preference
  const handleResetLoginPreference = () => {
    localStorage.removeItem('continueWithoutLogin');
    // Show the login modal again
    if ((window as any).netlifyIdentity) {
      (window as any).netlifyIdentity.open('login');
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    } else {
      openSignup();
    }
  };

  const handleDashboardAccess = () => {
    navigate('/app/dashboard');
  };

  const handleSignIn = () => {
    openLogin();
  };

  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/horlogo.png" alt="Ingenious" className="header-logo" />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="header-nav desktop-nav">
              <a href="#about" className="nav-link">About</a>
              <a href="#services" className="nav-link">Services</a>
              <a href="#contact" className="nav-link">Contact</a>
              {!isAuthenticated ? (
                <div className="auth-buttons">
                  <button onClick={handleSignIn} className="btn btn-outline">
                    Sign In
                  </button>
                  <button onClick={handleGetStarted} className="btn btn-primary">
                    Sign Up
                  </button>
                </div>
              ) : (
                <div className="user-menu">
                  <button onClick={handleDashboardAccess} className="btn btn-primary">
                    Dashboard
                  </button>
                  <button onClick={handleLogout} className="btn btn-outline">
                    Logout
                  </button>
                </div>
              )}
            </nav>
            
            {/* Mobile Hamburger Menu */}
            <div className="mobile-nav">
              <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
              </button>
            </div>
          </div>
          
                    {/* Mobile Menu Overlay */}
          <div 
            className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <nav className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              {/* Close Button */}
              <div className="mobile-menu-header">
                <button 
                  className="mobile-close-btn" 
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <span className="close-icon">✕</span>
                </button>
              </div>
              
              <div className="mobile-menu-content">
                <a href="#about" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  About
                </a>
                <a href="#services" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Services
                </a>
                <a href="#contact" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Contact
                </a>
                {!isAuthenticated ? (
                  <div className="mobile-auth-buttons">
                    <button onClick={() => { 
                      openLogin();
                      setMobileMenuOpen(false); 
                    }} className="btn btn-outline btn-mobile">
                      Sign In
                    </button>
                    <button onClick={() => { 
                      openSignup();
                      setMobileMenuOpen(false); 
                    }} className="btn btn-primary btn-mobile">
                      Sign Up
                    </button>
                  </div>
                ) : (
                  <div className="mobile-user-menu">
                    <button onClick={() => { navigate('/app/dashboard'); setMobileMenuOpen(false); }} className="btn btn-primary btn-mobile">
                      Dashboard
                    </button>
                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="btn btn-outline btn-mobile">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span>🚀 AI-Powered Platform</span>
            </div>
            <h1 className="hero-title">
              Supercharge Your{' '}
              <span className="highlight">Productivity</span>
              <br />
              with AI Magic
            </h1>
            <p className="hero-subtitle">
              Experience the future of work with our cutting-edge AI tools. 
              Generate content, solve problems, and create amazing things in seconds.
            </p>
            
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">10+</span>
                <span className="stat-label">AI Tools</span>
              </div>
              <div className="stat">
                <span className="stat-number">10K+</span>
                <span className="stat-label">Users Support</span>
              </div>
              <div className="stat">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">Uptime</span>
              </div>
            </div>
            
            <div className="hero-actions">
              {!isAuthenticated ? (
                <>
                  <button onClick={handleGetStarted} className="btn btn-primary btn-large">
                    <span>🚀 Start Building</span>
                    {/* <span className="btn-subtitle">Free Forever</span> */}
                  </button>
                  <button onClick={handleSignIn} className="btn btn-secondary btn-large">
                    Sign In
                  </button>
                </>
              ) : (
                <button onClick={handleDashboardAccess} className="btn btn-primary btn-large">
                  Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-card card-1">
            <span>✨</span>
            <p>AI Content</p>
          </div>
          <div className="floating-card card-2">
            <span>🎯</span>
            <p>Smart Tools</p>
          </div>
          <div className="floating-card card-3">
            <span>⚡</span>
            <p>Fast Results</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Powerful AI Tools</h2>
            <p className="section-subtitle">Everything you need to create, learn, and grow</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🤖</div>
              <h3>Content Creation</h3>
              <p>Generate blogs, emails, social media posts, and more with our advanced AI writing assistant</p>
              <ul className="service-features">
                <li>✨ SEO-optimized content</li>
                <li>📝 Multiple writing styles</li>
                <li>🚀 Instant generation</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">📚</div>
              <h3>Learning & Education</h3>
              <p>Create quizzes, get essay feedback, and accelerate your learning with AI-powered tools</p>
              <ul className="service-features">
                <li>🎯 Personalized learning</li>
                <li>📊 Progress tracking</li>
                <li>💡 Smart explanations</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">🎨</div>
              <h3>Creative Suite</h3>
              <p>Unleash your creativity with AI-powered story generation, poetry, and art prompts</p>
              <ul className="service-features">
                <li>🌟 Unique ideas</li>
                <li>🎭 Multiple genres</li>
                <li>💫 Inspiration on demand</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">💼</div>
              <h3>Business Solutions</h3>
              <p>Boost your business with AI-generated product descriptions, marketing copy, and more</p>
              <ul className="service-features">
                <li>📈 Conversion focused</li>
                <li>🎯 Target audience</li>
                <li>⚡ Quick turnaround</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">⚡</div>
              <h3>Utility Tools</h3>
              <p>Summarize text, translate languages, and optimize your workflow with smart utilities</p>
              <ul className="service-features">
                <li>🔍 Smart analysis</li>
                <li>🌍 Multi-language</li>
                <li>⚙️ Workflow automation</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">💬</div>
              <h3>AI Assistant</h3>
              <p>Get instant help with any task, question, or creative project from our AI companion</p>
              <ul className="service-features">
                <li>🤝 24/7 availability</li>
                <li>🧠 Context aware</li>
                <li>🎯 Task focused</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Ingenious?</h2>
            <p className="section-subtitle">We're not just another AI platform - we're your creative partner</p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <div className="about-feature">
                <div className="feature-icon-small">🚀</div>
                <div>
                  <h3>Lightning Fast</h3>
                  <p>Get results in seconds, not hours. Our AI processes your requests instantly.</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="feature-icon-small">🎯</div>
                <div>
                  <h3>Precision Focused</h3>
                  <p>Every tool is designed for specific use cases, ensuring the best possible results.</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="feature-icon-small">🔒</div>
                <div>
                  <h3>Enterprise Security</h3>
                  <p>Your data is protected with bank-level security and privacy controls.</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="feature-icon-small">💡</div>
                <div>
                  <h3>Always Learning</h3>
                  <p>Our AI models continuously improve, giving you better results every day.</p>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="tech-stack">
                <div className="tech-item">AI/ML</div>
                <div className="tech-item">NLP</div>
                <div className="tech-item">Cloud</div>
                <div className="tech-item">API</div>
                <div className="tech-item">Security</div>
                <div className="tech-item">Analytics</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Users Say</h2>
            <p className="section-subtitle">Join thousands of satisfied users worldwide</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"Ingenious has completely transformed my content creation workflow. I can now produce 10x more content in half the time!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👩‍💼</div>
                <div className="author-info">
                  <h4>Sarah Chen</h4>
                  <p>Content Creator</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"The AI tools are incredibly accurate and save me hours every week. It's like having a brilliant assistant available 24/7."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👨‍💻</div>
                <div className="author-info">
                  <h4>Marcus Rodriguez</h4>
                  <p>Software Developer</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"As a student, these tools have been a game-changer for my studies. The quiz generation and essay feedback are invaluable."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👩‍🎓</div>
                <div className="author-info">
                  <h4>Emma Thompson</h4>
                  <p>Graduate Student</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Get In Touch</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Ready to get started?</h3>
              <p>Join thousands of users who are already transforming their workflow with AI.</p>
              <div className="contact-details">
                {/* <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <span>support@ingenious.ai</span>
                </div> */}
                <div className="contact-item">
                  <span className="contact-icon">🌐</span>
                  <span>ingenious.rivio.ca</span>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <form>
                <div className="form-group">
                  <input type="text" placeholder="Your Name" className="form-input" />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Your Email" className="form-input" />
                </div>
                <div className="form-group">
                  <textarea placeholder="Your Message" className="form-textarea" rows={4}></textarea>
                </div>
                <button type="submit" className="btn btn-primary">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Ready to Transform Your Workflow?</h2>
          <p>Join thousands of users who are already using AI to boost their productivity</p>
          <button onClick={handleGetStarted} className="btn btn-primary btn-large">
            Get Started Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2024 Ingenious AI Tools. All rights reserved.</p>
            {!isAuthenticated && (
              <div className="footer-auth-links">
                <button 
                  onClick={handleResetLoginPreference} 
                  className="footer-auth-link"
                >
                  Sign In
                </button>
                <span className="footer-separator">•</span>
                <button 
                  onClick={() => {
                    localStorage.removeItem('continueWithoutLogin');
                    openSignup();
                  }} 
                  className="footer-auth-link"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
